import { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Card } from '@/components/ui/card';
import { MapPin, Search, X, Navigation, Loader2 } from 'lucide-react';

export default function LocationFilter({ onFilterChange, unitPreference = 'metric' }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const isMetric = unitPreference === 'metric';
  const maxDistance = isMetric ? 50 : 30; // 50km or 30 miles
  const defaultDistance = isMetric ? 10 : 6; // 10km or 6 miles
  const [radius, setRadius] = useState([defaultDistance]);
  const [coordinates, setCoordinates] = useState(null);
  const [locationName, setLocationName] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [useGPS, setUseGPS] = useState(false);
  const [gpsRequested, setGpsRequested] = useState(false);
  const [loadingGPS, setLoadingGPS] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Request GPS on mount
  useEffect(() => {
    if (!gpsRequested) {
      requestGPSLocation();
      setGpsRequested(true);
    }
  }, []);

  const requestGPSLocation = () => {
    if ('geolocation' in navigator) {
      setLoadingGPS(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const coords = {
            lat: position.coords.latitude,
            lon: position.coords.longitude
          };
          setCoordinates(coords);
          setUseGPS(true);
          setIsActive(true);
          
          // Reverse geocode to get location name
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.lat}&lon=${coords.lon}&zoom=10`,
              { headers: { 'Accept-Language': 'en' } }
            );
            const data = await response.json();
            const name = data.address?.city || data.address?.town || data.address?.suburb || 'Your Location';
            setLocationName(name);
          } catch {
            setLocationName('Your Location');
          }
          
          onFilterChange({
            active: true,
            locationName: 'Your Location',
            coordinates: coords,
            radiusMiles: isMetric ? radius[0] * 0.621371 : radius[0],
            isGPS: true
          });
          setLoadingGPS(false);
        },
        (error) => {
          console.log('GPS denied:', error);
          setUseGPS(false);
          setLoadingGPS(false);
        },
        { timeout: 10000 }
      );
    }
  };

  // Search for locations using Nominatim (OpenStreetMap)
  const handleSearch = (query) => {
    setSearchQuery(query);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }
    
    searchTimeoutRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const data = await response.json();
        setSearchResults(data);
      } catch (error) {
        console.error('Search failed:', error);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const handleResultClick = (result) => {
    const coords = {
      lat: parseFloat(result.lat),
      lon: parseFloat(result.lon)
    };
    const name = result.display_name.split(',')[0];
    
    setCoordinates(coords);
    setLocationName(name);
    setSearchQuery(name);
    setSearchResults([]);
    setIsActive(true);
    setUseGPS(false);
    
    onFilterChange({
      active: true,
      locationName: name,
      coordinates: coords,
      radiusMiles: isMetric ? radius[0] * 0.621371 : radius[0],
      isGPS: false
    });
  };

  const handleRadiusChange = (value) => {
    setRadius(value);
    if (isActive && coordinates) {
      onFilterChange({
        active: true,
        locationName: locationName,
        coordinates: coordinates,
        radiusMiles: isMetric ? value[0] * 0.621371 : value[0],
        isGPS: useGPS
      });
    }
  };

  const handleClear = () => {
    setSearchQuery('');
    setSearchResults([]);
    setRadius([defaultDistance]);
    setCoordinates(null);
    setLocationName('');
    setIsActive(false);
    setUseGPS(false);
    onFilterChange({ active: false });
  };

  return (
    <Card className="p-4 mb-6">
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <MapPin className="w-5 h-5 text-purple-500" />
          <h3 className="font-semibold text-foreground">Find Rides Near You</h3>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {/* Location Search or GPS Status */}
          <div className="md:col-span-1 relative">
            <Label>
              {useGPS ? 'Using GPS Location' : 'Search Location'}
            </Label>
            {useGPS ? (
              <div className="flex items-center gap-2 p-2 bg-green-500/20 border border-green-500/30 rounded-md mt-1">
                <Navigation className="w-4 h-4 text-green-500" />
                <span className="text-sm text-green-500 truncate">{locationName}</span>
              </div>
            ) : (
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Type city, town, or area..."
                  className="pl-10"
                  data-testid="location-search-input"
                />
                {searching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
                )}
                
                {/* Search results dropdown */}
                {searchResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {searchResults.map((result, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleResultClick(result)}
                        className="w-full px-4 py-3 text-left hover:bg-accent border-b border-border last:border-b-0"
                      >
                        <p className="text-sm text-foreground truncate">{result.display_name}</p>
                        <p className="text-xs text-muted-foreground">{result.type}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Radius Slider */}
          <div className="md:col-span-2">
            <Label>
              Distance: {radius[0]} {isMetric ? 'km' : 'miles'}
            </Label>
            <div className="flex items-center gap-4 pt-2 mt-1">
              <span className="text-sm text-muted-foreground">0</span>
              <Slider
                value={radius}
                onValueChange={handleRadiusChange}
                min={1}
                max={maxDistance}
                step={1}
                className="flex-1"
                data-testid="location-search-radius"
              />
              <span className="text-sm text-muted-foreground">{maxDistance}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          {isActive && (
            <Button
              variant="outline"
              onClick={handleClear}
              data-testid="location-clear-button"
            >
              <X className="w-4 h-4 mr-2" />
              Clear
            </Button>
          )}
          {!useGPS && (
            <Button
              variant="outline"
              onClick={requestGPSLocation}
              disabled={loadingGPS}
              data-testid="use-gps-button"
            >
              {loadingGPS ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Navigation className="w-4 h-4 mr-2" />
              )}
              Use GPS
            </Button>
          )}
        </div>

        {/* Active Filter Display */}
        {isActive && coordinates && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-purple-500/10 border border-purple-500/20 rounded-md p-2">
            <MapPin className="w-4 h-4 text-purple-500" />
            <span>
              Showing rides within <strong>{radius[0]} {isMetric ? 'km' : 'miles'}</strong> of <strong>{locationName}</strong>
            </span>
          </div>
        )}
      </div>
    </Card>
  );
}
